import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import {
    Search, Bell, LogOut, ChevronDown, Plus, Menu, User, Shield,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import CommandPalette from "@/components/CommandPalette";
import CustomerProvisioningWizard from "@/pages/admin/CustomerProvisioningWizard";

/**
 * Global Topbar.
 *
 * Two modes:
 *   - Restaurant mode (corporate / restaurant users):
 *       restaurant switcher · today · kitchen search · kitchen quick actions.
 *   - Super Admin mode (super_admin users):
 *       platform pill · platform search · SaaS quick actions.
 *
 * Super Admin never sees restaurant kitchen context.
 */
// Same order as before ("Monday, October 19"); only weekday/month names follow the language.
function topbarDate(d, lang) {
    if (lang !== "fi") return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    const wd = new Intl.DateTimeFormat("fi-FI", { weekday: "long" }).format(d);
    const mo = new Intl.DateTimeFormat("fi-FI", { month: "long" }).format(d);
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)}, ${mo} ${d.getDate()}`;
}

export default function Topbar({ onOpenMobileNav }) {
    const { t, i18n } = useTranslation();
    const { user, restaurants, currentRestaurant, selectRestaurant, logout } = useApp();
    const navigate = useNavigate();
    const [isMac, setIsMac] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const isSuperAdmin = user?.role === "super_admin";

    const today = topbarDate(new Date(), i18n.language);

    // ⌘K / Ctrl+K opens the global command palette.
    useEffect(() => {
        setIsMac(typeof navigator !== "undefined"
                  && /Mac|iPhone|iPad/.test(navigator.platform));
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen(true);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <header
            data-testid="topbar"
            className="h-16 border-b border-neutral-200 bg-white px-4 lg:px-6 flex items-center gap-3 lg:gap-4"
        >
            {/* Hamburger — mobile only */}
            <button
                data-testid="topbar-menu-button"
                onClick={onOpenMobileNav}
                aria-label={t("nav.openNavigation", "Open navigation")}
                className="lg:hidden h-10 w-10 rounded-md hover:bg-neutral-100 grid place-items-center -ml-1"
            >
                <Menu className="h-5 w-5" />
            </button>

            {isSuperAdmin ? (
                <div
                    data-testid="platform-pill"
                    className="hidden sm:inline-flex items-center gap-2 px-3 h-9 rounded-md border border-neutral-900 bg-neutral-900 text-white text-xs font-semibold tracking-wide uppercase"
                >
                    MiseOne · Platform
                </div>
            ) : (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="restaurant-switcher"
                            className="flex items-center gap-2 px-3 py-2 rounded-md border border-neutral-200 hover:bg-neutral-50 text-sm font-medium"
                        >
                            <span className="text-neutral-500">{t("topbar.restaurant", "Restaurant")}</span>
                            <span className="font-semibold text-neutral-900">
                                {currentRestaurant?.name || "—"}
                            </span>
                            <ChevronDown className="h-4 w-4 text-neutral-500" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                        <DropdownMenuLabel>{t("topbar.switchRestaurant", "Switch restaurant")}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {restaurants.map((r) => (
                            <DropdownMenuItem
                                key={r.id}
                                data-testid={`restaurant-option-${r.name.toLowerCase()}`}
                                onClick={() => selectRestaurant(r.id)}
                            >
                                <span className="font-medium">{r.name}</span>
                                <span className="ml-auto text-xs text-neutral-500">{r.city}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            <div className="hidden md:flex items-center text-sm text-neutral-500">
                <span data-testid="today-date">{today}</span>
            </div>

            {/* Search trigger — opens the command palette */}
            <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                data-testid="global-search-input"
                className="ml-4 flex-1 min-w-0 max-w-xl h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 hover:bg-white focus:bg-white focus:border-neutral-900 focus:outline-none text-sm text-neutral-400"
            >
                <Search className="h-4 w-4" />
                <span className="flex-1 min-w-0 truncate text-left">
                    {isSuperAdmin
                        ? t("topbar.searchPlatform", "Search companies, users, subscriptions, support…")
                        : t("topbar.searchKitchen", "Search recipes, products, suppliers, tasks…")}
                </span>
                <kbd
                    data-testid="search-shortcut-hint"
                    className="hidden md:flex h-6 px-1.5 items-center text-[10px] font-semibold text-neutral-500 bg-white border border-neutral-200 rounded"
                >
                    {isMac ? "⌘K" : "Ctrl K"}
                </kbd>
            </button>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

            {/* Quick actions */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        data-testid="quick-action-button"
                        className="flex items-center gap-2 h-10 px-3 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                    >
                        <Plus className="h-4 w-4" />
                        <span className="hidden md:inline">
                            {isSuperAdmin ? t("topbar.new", "New") : t("topbar.quickAction", "Quick Action")}
                        </span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    {isSuperAdmin ? (
                        <>
                            <DropdownMenuLabel>{t("topbar.platformActions", "Platform actions")}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem data-testid="qa-new-company"
                                onClick={() => setWizardOpen(true)}>
                                {t("topbar.newCompany", "New Company")}
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-new-restaurant"
                                onClick={() => navigate("/admin?tab=restaurants&new=1")}>
                                {t("topbar.newRestaurant", "New Restaurant")}
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-new-user"
                                onClick={() => navigate("/admin?tab=users&new=1")}>
                                {t("topbar.newUser", "New User")}
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-new-plan"
                                onClick={() => navigate("/admin?tab=plans&new=1")}>
                                {t("topbar.newPlan", "New Plan")}
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-new-subscription"
                                onClick={() => navigate("/admin?tab=subscriptions&new=1")}>
                                {t("topbar.newSubscription", "New Subscription")}
                            </DropdownMenuItem>
                        </>
                    ) : (
                        <>
                            <DropdownMenuItem data-testid="qa-upload-delivery"
                                onClick={() => navigate("/deliveries")}>{t("topbar.uploadDelivery", "Upload Delivery")}</DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-weekly-menu"
                                onClick={() => navigate("/weekly-menu")}>{t("topbar.openWeeklyMenu", "Open Weekly Menu")}</DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-add-recipe"
                                onClick={() => navigate("/recipes")}>{t("topbar.addRecipe", "Add Recipe")}</DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-start-inventory"
                                onClick={() => navigate("/inventory")}>{t("topbar.startInventory", "Start Inventory")}</DropdownMenuItem>
                            <DropdownMenuItem data-testid="qa-record-temp"
                                onClick={() => navigate("/daily-checks")}>{t("topbar.recordTemperature", "Record Temperature")}</DropdownMenuItem>
                            {user?.role === "corporate" && (
                                <DropdownMenuItem data-testid="qa-announcement"
                                    onClick={() => navigate("/announcements")}>{t("topbar.newAnnouncement", "New Announcement")}</DropdownMenuItem>
                            )}
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <button
                data-testid="notifications-button"
                className="h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-neutral-100"
                title={t("topbar.notifications", "Notifications")}
                onClick={() => isSuperAdmin ? navigate("/admin?tab=notifications") : undefined}
            >
                <Bell className="h-4 w-4" />
            </button>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        data-testid="user-menu-button"
                        className="h-10 px-3 inline-flex items-center gap-2 rounded-md hover:bg-neutral-100 text-sm"
                    >
                        <div className="h-7 w-7 rounded-full bg-neutral-900 text-white grid place-items-center text-xs font-semibold">
                            {user?.name?.charAt(0) || "U"}
                        </div>
                        <span className="hidden md:inline">{user?.name}</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                        <div className="text-sm font-medium">{user?.name}</div>
                        <div className="text-xs text-neutral-500 capitalize">
                            {isSuperAdmin ? "Super Admin · MiseOne" : t(`roles.${user?.role}`, user?.role || "")}
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="my-account-link" onClick={() => navigate("/me")}>
                        <User className="h-4 w-4 mr-2" /> {t("topbar.myAccount", "My account")}
                    </DropdownMenuItem>
                    {isSuperAdmin && (
                        <DropdownMenuItem data-testid="platform-users-link"
                                            onClick={() => navigate("/admin/platform-users")}>
                            <Shield className="h-4 w-4 mr-2" /> {t("topbar.platformUsers", "Platform users")}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="logout-button" onClick={logout}>
                        <LogOut className="h-4 w-4 mr-2" /> {t("topbar.signOut", "Sign out")}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {isSuperAdmin && wizardOpen && (
                <CustomerProvisioningWizard
                    onClose={() => setWizardOpen(false)}
                    onCreated={() => {
                        setWizardOpen(false);
                        navigate("/admin?tab=companies");
                    }}
                />
            )}
        </header>
    );
}
